import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../config/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import './Commission.css';

const unitLogo = '/images/LP_Unit_logo.png';

// Helmet options
const helmetOptions = [
  'Ops-Core FTHS Tan (AFSOC Only)',
  'Ops-Core FTHS Multicam',
  'Other/Please Specify'
];

const railOptions = [
  'Arc Rails',
  'No Rails',
  'Rail Link (Hyun - additional cost)'
];

const coverOptions = [
  'Ops-Core FTHS Cover',
  'Ops-Core Maritime Cover',
  'First Spear Cover',
  'Orion Design Group Cover'
];

const batteryOptions = [
  'L3Harris Battery Pack Black',
  'L3Harris Battery Pack MC',
  'Ops-Core Counterweight Pack (in conjunction w/ L3Harris Battery Pack)',
  'Ferro Concepts Battery Retention System',
  'TNVC Mohawk',
  'TYR Tactical Battery Pouch',
  'Shaw Concepts BRAIN Pouch'
];

const strobeOptions = {
  'Core Survival': ['Helstar 5 EXO', 'Helstar 6 Gen II', 'Helstar 6 Gen III'],
  'S&S Precision Manta Strobe': null,
  'Rail Link Strobe (Rail Link only)': null,
  'Single V-Lite (Multi-Color)': null,
  'Double V-Lite (Single Color)': null
};

const earProOptions = {
  'Peltor': ['Comtac IV', 'Comtac V', 'Comtac VI'],
  'Ops-Core AMP': null
};

const taskLightOptions = {
  'Princeton Tec': ['MPLS Tan', 'MPLS Black', 'Charge Pro Tan'],
  'Surefire M300': ['M-Ax Mount (Tan)', 'M-Ax Mount (Black)', 'M-Ax Mount (Mixed)']
};

const taskLightSideOptions = [
  'Left',
  'Right',
  'Left Rear (Umbrella)',
  'Right Rear (Umbrella)'
];

const nodMountOptions = {
  'Wilcox L4G24': ['Tan (AFSOC Only)', 'Black (AFSOC Only)', 'Silver (AFSOC Only)', 'GSGM (CAG Only)'],
  'Swift Universal FPG-21 (Akcent Version)': null
};

const nodOptions = [
  'L3Harris GPNVG-18',
  'L3Harris AN/PVS-31A',
  'L3Harris F-PANO (Old Version)',
  'L3Harris BNVD-F (Binocular Night Vision Device - Fused)'
];

function HelmetCommission() {
  const { user, userData } = useAuth();
  
  // Info popup state
  const [showInfoPopup, setShowInfoPopup] = useState(true);
  const [zap, setZap] = useState('');
  const [discordName, setDiscordName] = useState('');
  const [paypalEmail, setPaypalEmail] = useState('');
  const [infoError, setInfoError] = useState('');
  
  // Selection state
  const [selectedHelmet, setSelectedHelmet] = useState('');
  const [selectedRails, setSelectedRails] = useState('');
  const [selectedCover, setSelectedCover] = useState('');
  const [selectedBattery, setSelectedBattery] = useState('');
  
  const [selectedStrobe, setSelectedStrobe] = useState('');
  const [selectedStrobeModel, setSelectedStrobeModel] = useState('');
  const [expandedStrobe, setExpandedStrobe] = useState('');
  
  const [selectedEarPro, setSelectedEarPro] = useState('');
  const [selectedEarProModel, setSelectedEarProModel] = useState('');
  const [expandedEarPro, setExpandedEarPro] = useState('');
  
  const [selectedTaskLight, setSelectedTaskLight] = useState('');
  const [selectedTaskLightModel, setSelectedTaskLightModel] = useState('');
  const [expandedTaskLight, setExpandedTaskLight] = useState('');
  
  const [selectedTaskLightSide, setSelectedTaskLightSide] = useState('');
  
  const [selectedNodMount, setSelectedNodMount] = useState('');
  const [selectedNodMountModel, setSelectedNodMountModel] = useState('');
  const [expandedNodMount, setExpandedNodMount] = useState('');
  
  const [selectedNod, setSelectedNod] = useState('');
  
  const [additionalOptions, setAdditionalOptions] = useState('');
  const [comments, setComments] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Other/Please Specify
  const [otherHelmet, setOtherHelmet] = useState('');

  // Premium options
  const [premiumAsset, setPremiumAsset] = useState(false);
  const [customAcex, setCustomAcex] = useState(false);

  const BASE_PRICE = 30;
  const PREMIUM_ASSET_PRICE = 10;
  const ACEX_PRICE = 10;

  const calculateTotal = () => {
    let total = BASE_PRICE;
    if (premiumAsset) total += PREMIUM_ASSET_PRICE;
    if (customAcex) total += ACEX_PRICE;
    return total;
  };

  // Check if GSGM is selected (locks NOD to GPNVG-18 only)
  const isGSGMSelected = selectedNodMountModel === 'GSGM (CAG Only)';

  const handleInfoSubmit = (e) => {
    e.preventDefault();
    setInfoError('');
    
    if (!zap.trim()) {
      setInfoError('ZAP is required');
      return;
    }
    if (!discordName.trim()) {
      setInfoError('Discord Name is required');
      return;
    }
    if (!paypalEmail.trim() || !paypalEmail.includes('@')) {
      setInfoError('Valid PayPal Email is required');
      return;
    }
    
    setShowInfoPopup(false);
  };

  // Generic handlers for nested options
  const handleNestedClick = (item, options, expanded, setExpanded, setSelected, setModel) => {
    if (options[item]) {
      setExpanded(expanded === item ? '' : item);
    } else {
      setSelected(item);
      setModel('');
      setExpanded('');
    }
  };

  const handleNestedModelClick = (item, model, setSelected, setModel, setExpanded) => {
    setSelected(item);
    setModel(model);
    setExpanded('');
    
    // If GSGM is selected, auto-select GPNVG-18
    if (model === 'GSGM (CAG Only)') {
      setSelectedNod('L3Harris GPNVG-18');
    }
  };

  const getDisplaySelection = (item, model) => {
    if (model) return `${item} - ${model}`;
    return item;
  };

  const handleSubmitCommission = async (e) => {
    e.preventDefault();
    
    if (!selectedHelmet) {
      alert('Please select a helmet type');
      return;
    }
    
    setSubmitting(true);
    
    try {
      await addDoc(collection(db, 'requests'), {
        userId: user.uid,
        userEmail: userData?.email || '',
        username: userData?.username || userData?.email || '',
        type: 'New - Helmet',
        status: 'Unassigned',
        zap: zap,
        discordName: discordName,
        paypalEmail: paypalEmail,
        helmet: selectedHelmet === 'Other/Please Specify' ? `Other: ${otherHelmet}` : selectedHelmet,
        rails: selectedRails || 'None',
        cover: selectedCover || 'None',
        batteryPack: selectedBattery || 'None',
        strobe: getDisplaySelection(selectedStrobe, selectedStrobeModel) || 'None',
        earProtection: getDisplaySelection(selectedEarPro, selectedEarProModel) || 'None',
        taskLight: getDisplaySelection(selectedTaskLight, selectedTaskLightModel) || 'None',
        taskLightSide: selectedTaskLightSide || 'None',
        nodMount: getDisplaySelection(selectedNodMount, selectedNodMountModel) || 'None',
        nod: selectedNod || 'None',
        additionalOptions: additionalOptions,
        premiumAsset: premiumAsset,
        customAcex: customAcex,
        totalPrice: calculateTotal(),
        comments: comments,
        createdAt: serverTimestamp()
      });
      
      setSubmitSuccess(true);
    } catch (error) {
      console.error('Error submitting commission:', error);
      alert('Error submitting commission. Please try again.');
    }
    
    setSubmitting(false);
  };

  // Render nested option section
  const renderNestedOptions = (title, options, selected, selectedModel, expanded, setExpanded, setSelected, setModel) => (
    <div className="dropdown-section">
      <div className="dropdown-header">{title}</div>
      <div className="options-list">
        {Object.keys(options).map((item) => (
          <div key={item} className="option-wrapper">
            <button
              type="button"
              className={`option-btn ${selected === item ? 'selected' : ''} ${expanded === item ? 'expanded' : ''}`}
              onClick={() => handleNestedClick(item, options, expanded, setExpanded, setSelected, setModel)}
            >
              {item}
              {options[item] && <span className="arrow">→</span>}
            </button>
            
            {options[item] && expanded === item && (
              <div className="sub-options">
                {options[item].map((model) => (
                  <button
                    key={model}
                    type="button"
                    className={`sub-option-btn ${selectedModel === model && selected === item ? 'selected' : ''}`}
                    onClick={() => handleNestedModelClick(item, model, setSelected, setModel, setExpanded)}
                  >
                    {model}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  // Render simple option section
  const renderSimpleOptions = (title, options, selected, setSelected, disabled = false) => (
    <div className="dropdown-section">
      <div className="dropdown-header">{title}</div>
      <div className="options-list">
        {options.map((item) => {
          const isDisabled = disabled && item !== 'L3Harris GPNVG-18';
          return (
            <button
              key={item}
              type="button"
              className={`option-btn ${selected === item ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
              onClick={() => !isDisabled && setSelected(item)}
              disabled={isDisabled}
            >
              {item}
              {isDisabled && <span className="lock-icon">🔒</span>}
            </button>
          );
        })}
      </div>
      {disabled && (
        <div className="gsgm-notice">
          GSGM mount is only compatible with GPNVG-18
        </div>
      )}
    </div>
  );

  if (submitSuccess) {
    return (
      <div className="commission-page" style={{ backgroundImage: `url(/images/new_build_background_photo.png)`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed' }}>
        <nav className="commission-navbar">
          <Link to="/" className="nav-left">
            <img src={unitLogo} alt="Logo" className="unit-logo" />
            <h1 className="site-title">Creature Supply</h1>
          </Link>
        </nav>
        <div className="success-container">
          <div className="success-card">
            <div className="success-icon">✓</div>
            <h2>Commission Submitted!</h2>
            <p>Your helmet commission has been received.</p>
            <p className="success-details">ZAP: {zap}</p>
            <p className="success-details">We'll notify you when your order is ready.</p>
            <Link to="/" className="btn-home">Return Home</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="commission-page" style={{ backgroundImage: `url(/images/new_build_background_photo.png)`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed' }}>
      {/* Navigation */}
      <nav className="commission-navbar">
        <Link to="/" className="nav-left">
          <img src={unitLogo} alt="Logo" className="unit-logo" />
          <h1 className="site-title">Creature Supply</h1>
        </Link>
        <div className="nav-right">
          <Link to="/" className="nav-link">Home</Link>
        </div>
      </nav>

      {/* Info Popup */}
      {showInfoPopup && (
        <div className="popup-overlay">
          <div className="popup-card">
            {infoError && <div className="popup-error">{infoError}</div>}
            
            <form onSubmit={handleInfoSubmit}>
              <div className="popup-field">
                <label>Your ZAP Number</label>
                <input
                  type="text"
                  value={zap}
                  onChange={(e) => setZap(e.target.value)}
                  placeholder="Enter your ZAP"
                  required
                />
              </div>
              <div className="popup-field">
                <label>Discord Name</label>
                <input
                  type="text"
                  value={discordName}
                  onChange={(e) => setDiscordName(e.target.value)}
                  placeholder="J. Smith"
                  required
                />
              </div>
              <div className="popup-field">
                <label>PayPal Email</label>
                <input
                  type="email"
                  value={paypalEmail}
                  onChange={(e) => setPaypalEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                />
              </div>
              <button type="submit" className="popup-btn">Continue</button>
            </form>
          </div>
        </div>
      )}

      {/* Main Content */}
      {!showInfoPopup && (
        <main className="commission-content">
          <div className="commission-layout">
            {/* Left Side - Selection Panel */}
            <div className="selection-panel helmet-panel">
              
              {/* Helmet */}
              {renderSimpleOptions('Helmet', helmetOptions, selectedHelmet, setSelectedHelmet)}
              {selectedHelmet === 'Other/Please Specify' && (
                <div className="dropdown-section" style={{ marginTop: '-0.5rem' }}>
                  <input
                    type="text"
                    value={otherHelmet}
                    onChange={(e) => setOtherHelmet(e.target.value)}
                    placeholder="Please specify helmet type..."
                    className="other-input"
                  />
                </div>
              )}
              
              {/* Rails */}
              {renderSimpleOptions('Rails', railOptions, selectedRails, setSelectedRails)}
              
              {/* Helmet Cover */}
              {renderSimpleOptions('Helmet Cover', coverOptions, selectedCover, setSelectedCover)}
              
              {/* Battery Pack / Counter-Weight */}
              {renderSimpleOptions('Battery Pack / Counter-Weight', batteryOptions, selectedBattery, setSelectedBattery)}
              
              {/* Strobe */}
              {renderNestedOptions('Strobe', strobeOptions, selectedStrobe, selectedStrobeModel, expandedStrobe, setExpandedStrobe, setSelectedStrobe, setSelectedStrobeModel)}
              
              {/* Ear Protection */}
              {renderNestedOptions('Ear Protection', earProOptions, selectedEarPro, selectedEarProModel, expandedEarPro, setExpandedEarPro, setSelectedEarPro, setSelectedEarProModel)}
              
              {/* Task Light */}
              {renderNestedOptions('Task Light', taskLightOptions, selectedTaskLight, selectedTaskLightModel, expandedTaskLight, setExpandedTaskLight, setSelectedTaskLight, setSelectedTaskLightModel)}
              
              {/* Task Light Side */}
              {renderSimpleOptions('Task Light Side', taskLightSideOptions, selectedTaskLightSide, setSelectedTaskLightSide)}
              
              {/* NOD Mount */}
              {renderNestedOptions('NOD Mount', nodMountOptions, selectedNodMount, selectedNodMountModel, expandedNodMount, setExpandedNodMount, setSelectedNodMount, setSelectedNodMountModel)}
              
              {/* NOD - with GSGM restriction */}
              {renderSimpleOptions('NOD', nodOptions, selectedNod, setSelectedNod, isGSGMSelected)}
              
              {/* Additional Options */}
              <div className="dropdown-section">
                <div className="dropdown-header">Additional Options</div>
                <textarea
                  value={additionalOptions}
                  onChange={(e) => setAdditionalOptions(e.target.value)}
                  placeholder="Please specify any additional options..."
                  className="comment-box"
                  rows={3}
                />
              </div>

              {/* Premium Options */}
              <div className="dropdown-section">
                <div className="dropdown-header">Additional Options</div>
                <div className="premium-options">
                  <label className="checkbox-option">
                    <input 
                      type="checkbox"
                      checked={premiumAsset}
                      onChange={(e) => setPremiumAsset(e.target.checked)}
                    />
                    <span className="checkbox-label">Premium Asset</span>
                    <span className="checkbox-price">+$10</span>
                  </label>
                  <label className="checkbox-option">
                    <input 
                      type="checkbox"
                      checked={customAcex}
                      onChange={(e) => setCustomAcex(e.target.checked)}
                    />
                    <span className="checkbox-label">Custom ACEX Options</span>
                    <span className="checkbox-price">+$10</span>
                  </label>
                </div>
              </div>

              {/* Comments */}
              <div className="dropdown-section">
                <textarea
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  placeholder="Please be as specific as possible with items and placement to give the armorers the best starting point"
                  className="comment-box"
                  rows={3}
                />
              </div>
            </div>

            {/* Right Side - Preview and Submit */}
            <div className="preview-panel">
              <div className="preview-header">Helmet Commission</div>
              
              <div className="preview-box">
                <div className="preview-placeholder">
                  <span>Select options to configure your helmet</span>
                </div>
              </div>
              
              {/* Selection Summary */}
              {selectedHelmet && (
                <div className="selection-summary">
                  <h3>Current Selection</h3>
                  <p><strong>Helmet:</strong> {selectedHelmet === 'Other/Please Specify' ? `Other: ${otherHelmet}` : selectedHelmet}</p>
                  {selectedRails && <p><strong>Rails:</strong> {selectedRails}</p>}
                  {selectedCover && <p><strong>Cover:</strong> {selectedCover}</p>}
                  {selectedBattery && <p><strong>Battery:</strong> {selectedBattery}</p>}
                  {selectedStrobe && <p><strong>Strobe:</strong> {getDisplaySelection(selectedStrobe, selectedStrobeModel)}</p>}
                  {selectedEarPro && <p><strong>Ear Pro:</strong> {getDisplaySelection(selectedEarPro, selectedEarProModel)}</p>}
                  {selectedTaskLight && <p><strong>Task Light:</strong> {getDisplaySelection(selectedTaskLight, selectedTaskLightModel)}</p>}
                  {selectedTaskLightSide && <p><strong>Light Side:</strong> {selectedTaskLightSide}</p>}
                  {selectedNodMount && <p><strong>NOD Mount:</strong> {getDisplaySelection(selectedNodMount, selectedNodMountModel)}</p>}
                  {selectedNod && <p><strong>NOD:</strong> {selectedNod}</p>}
                </div>
              )}

              {/* Cart Section */}
              <div className="cart-section">
                <h3 className="cart-title">Cart</h3>
                
                <div className="cart-items">
                  <div className="cart-line">
                    <span>New Helmet Commission (Base)</span>
                    <span>${BASE_PRICE}</span>
                  </div>
                </div>

                {premiumAsset && (
                  <div className="cart-line addon">
                    <span>Premium Asset</span>
                    <span>+${PREMIUM_ASSET_PRICE}</span>
                  </div>
                )}

                {customAcex && (
                  <div className="cart-line addon">
                    <span>Custom ACEX</span>
                    <span>+${ACEX_PRICE}</span>
                  </div>
                )}

                <div className="cart-total">
                  <span>Total</span>
                  <span>${calculateTotal()}</span>
                </div>
              </div>

              <button 
                type="button"
                onClick={handleSubmitCommission}
                className="submit-request-btn"
                disabled={submitting || !selectedHelmet}
              >
                {submitting ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </div>
        </main>
      )}
    </div>
  );
}

export default HelmetCommission;
